
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Music, DollarSign, Gift, CheckCircle, Info, Video, Camera } from 'lucide-react';
import SpotifyTrackSelector from '@/components/sequences/SpotifyTrackSelector';
import { SpotifyTrack } from '@/services/spotifyService';
import { enhancedSpotifyService } from '@/services/enhancedSpotifyService';
import { SequenceFormData } from '../types';
import GenresMultiSelect from '@/components/sequences/upload/GenresMultiSelect';
import LORSettingsSection from '../components/LORSettingsSection';
import LayoutPhotoUpload, { LayoutPhotoFile } from '@/components/sequences/upload/LayoutPhotoUpload';

interface DetailsStepProps {
  form: UseFormReturn<SequenceFormData>;
  selectedTrack: SpotifyTrack | null;
  onTrackSelect: (track: SpotifyTrack) => void;
  layoutPhotos: LayoutPhotoFile[];
  onLayoutPhotosChange: (photos: LayoutPhotoFile[]) => void;
  onNext: () => void;
  onBack: () => void;
}

const DetailsStep: React.FC<DetailsStepProps> = ({
  form,
  selectedTrack,
  onTrackSelect,
  layoutPhotos,
  onLayoutPhotosChange,
  onNext,
  onBack,
}) => {
  const [showSpotifySection, setShowSpotifySection] = useState(true);
  const [showLORSettings, setShowLORSettings] = useState(false);
  const isFree = form.watch('is_free');
  const price = form.watch('price') || 0;
  const softwareType = form.watch('software_type');

  // Auto-expand LOR settings when Light-O-Rama is selected
  React.useEffect(() => {
    if (softwareType === 'Light-O-Rama') {
      setShowLORSettings(true);
    }
  }, [softwareType]);

  const handleNext = async () => {
    const isValid = await form.trigger(['title', 'artist', 'software_type', 'holiday_type', 'is_free', 'price']);
    if (isValid) {
      onNext();
    }
  };

  const handleTrackSelect = async (track: SpotifyTrack) => {
    console.log('🎵 SPOTIFY TRACK SELECTED - DEBUG INFO:');
    console.log('Track:', track);
    console.log('Track name:', track.name);
    console.log('Track artists:', track.artists);
    console.log('Primary artist:', track.artists[0]);
    console.log('Primary artist ID:', track.artists[0]?.id);
    console.log('Primary artist name:', track.artists[0]?.name);
    console.log('Album:', track.album);
    
    onTrackSelect(track);
    
    // Auto-populate form fields from Spotify data
    form.setValue('title', track.name);
    form.setValue('artist', track.artists[0]?.name || '');
    
    // Convert duration from milliseconds to seconds
    if (track.duration_ms) {
      form.setValue('duration', Math.round(track.duration_ms / 1000));
    }
    
    // Set album info if available
    if (track.album) {
      form.setValue('album', track.album.name);
      if (track.album.images?.[0]?.url) {
        form.setValue('album_cover_url', track.album.images[0].url);
      }
    }

    // Enhanced genre detection with comprehensive logging
    console.log('🔍 STARTING ENHANCED GENRE DETECTION...');
    try {
      const detection = await enhancedSpotifyService.detectGenre(track);
      console.log('🎯 Enhanced genre detection result:', detection);
      
      if (detection) {
        console.log('✅ Enhanced detection successful!');
        console.log('Primary genre:', detection.genreName);
        console.log('Alternatives:', detection.alternatives);
        console.log('Confidence:', detection.confidence);
        console.log('Source:', detection.source);
        
        // Build genres array: primary + up to 5 alternatives (unique, lowercased titles preserved as names)
        const primary = detection.genreName;
        const alternatives = (detection.alternatives || []).map(a => a.genreName);
        const unique = Array.from(new Set([primary, ...alternatives])).slice(0, 6);
        
        console.log('🎵 Final genres array:', unique);
        form.setValue('genres', unique);
        form.setValue('primary_genre', primary);
        form.setValue('secondary_genre', unique[1] || undefined);
      } else {
        console.log('❌ Enhanced detection returned null - falling back to keyword detection');
        // Fallback to basic detection
        const fallbackGenre = detectGenreFallback(track);
        console.log('🔄 Fallback genre detected:', fallbackGenre);
        if (fallbackGenre) {
          form.setValue('genres', [fallbackGenre]);
          form.setValue('primary_genre', fallbackGenre);
          form.setValue('secondary_genre', undefined);
        }
      }
    } catch (error) {
      console.error('💥 ERROR in enhanced genre detection:', error);
      console.error('Error stack:', error.stack);
      // Fallback to basic detection
      const fallbackGenre = detectGenreFallback(track);
      console.log('🔄 Fallback genre after error:', fallbackGenre);
      if (fallbackGenre) {
        form.setValue('genres', [fallbackGenre]);
        form.setValue('primary_genre', fallbackGenre);
        form.setValue('secondary_genre', undefined);
      }
    }
    console.log('🏁 GENRE DETECTION COMPLETE');
  };

  const detectGenreFallback = (track: SpotifyTrack): string => {
    const title = track.name.toLowerCase();
    const artist = track.artists[0]?.name.toLowerCase() || '';
    const album = track.album.name.toLowerCase();
    
    // Basic fallback detection
    if (title.includes('christmas') || title.includes('xmas') || title.includes('holiday') || 
        album.includes('christmas') || album.includes('holiday')) {
      return 'Christmas';
    }
    
    if (title.includes('rock') || artist.includes('rock') || album.includes('rock')) {
      return 'Rock';
    }
    
    if (title.includes('pop') || artist.includes('pop') || album.includes('pop')) {
      return 'Pop';
    }
    
    if (title.includes('country') || artist.includes('country') || album.includes('country')) {
      return 'Country';
    }
    
    if (title.includes('jazz') || artist.includes('jazz') || album.includes('jazz')) {
      return 'Jazz';
    }
    
    if (title.includes('classical') || artist.includes('classical') || album.includes('classical')) {
      return 'Classical';
    }
    
    return 'Contemporary';
  };

  const calculatePlatformFee = (price: number): number => {
    return price * 0.10; // 10% platform fee
  };

  const calculateYourEarnings = (price: number): number => {
    return price - calculatePlatformFee(price);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add Details</CardTitle>
        <p className="text-sm text-muted-foreground">
          Provide basic information, optionally link to Spotify, and set your pricing
        </p>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <div className="space-y-8">
            
            {/* Spotify Integration Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Music className="h-5 w-5" />
                  Spotify Integration (Optional)
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSpotifySection(!showSpotifySection)}
                >
                  {showSpotifySection ? 'Hide' : 'Show'}
                </Button>
              </div>

              {(showSpotifySection || selectedTrack) && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Linking to Spotify provides better metadata and helps users discover your sequence.
                  </p>

                  {!selectedTrack ? (
                    <div className="space-y-4">
                      <SpotifyTrackSelector
                        onTrackSelect={handleTrackSelect}
                        selectedTrack={selectedTrack}
                        onClear={() => onTrackSelect(null!)}
                        placeholder="Search for the song on Spotify..."
                      />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium text-green-900">Spotify track linked!</p>
                            <div className="mt-2 space-y-1 text-sm text-green-800">
                              <p><strong>Track:</strong> {selectedTrack.name}</p>
                              <p><strong>Artist:</strong> {selectedTrack.artists.map(a => a.name).join(', ')}</p>
                              <p><strong>Album:</strong> {selectedTrack.album.name}</p>
                              {selectedTrack.duration_ms && (
                                <p><strong>Duration:</strong> {Math.floor(selectedTrack.duration_ms / 60000)}:{String(Math.floor((selectedTrack.duration_ms % 60000) / 1000)).padStart(2, '0')}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-center">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onTrackSelect(null!)}
                        >
                          Change Track
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <Separator />
            
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Info className="h-5 w-5" />
                Basic Information
              </h3>
              
               <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Song Title *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter song title" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="artist"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Artist Name *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter artist name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

               <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (seconds)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          value={field.value || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === '' ? undefined : Number(value));
                          }}
                          placeholder="Song duration in seconds"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Multi-genre selection with chips + searchable list */}
                <FormField
                  control={form.control}
                  name="genres"
                  render={() => (
                    <FormItem>
                      <FormLabel>Genres (up to 6)</FormLabel>
                      <FormControl>
                        <GenresMultiSelect form={form} max={6} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="primary_genre"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Genre</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Primary genre (must be in Genres)" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="secondary_genre"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Secondary Genre (optional)</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Secondary genre (must be in Genres)" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="software_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Software Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select software" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="xLights">xLights</SelectItem>
                          <SelectItem value="Light-O-Rama">Light-O-Rama</SelectItem>
                          <SelectItem value="Vixen">Vixen</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="holiday_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Holiday *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select holiday" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Christmas">Christmas</SelectItem>
                          <SelectItem value="Halloween">Halloween</SelectItem>
                          <SelectItem value="FourthOfJuly">4th of July</SelectItem>
                          <SelectItem value="Easter">Easter</SelectItem>
                          <SelectItem value="Thanksgiving">Thanksgiving</SelectItem>
                          <SelectItem value="Hanukkah">Hanukkah</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="file_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>File Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select file type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Sequence only">Sequence only</SelectItem>
                          <SelectItem value="Lyrics only">Lyrics only</SelectItem>
                          <SelectItem value="Sequence & Lyrics">Sequence & Lyrics</SelectItem>
                          <SelectItem value="Singing Face">Singing Face</SelectItem>
                          <SelectItem value="Matrix Video">Matrix Video</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Describe your sequence (optional)..."
                        className="min-h-20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="video_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Video className="h-4 w-4" />
                      Video URL (Optional)
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="url"
                        placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                      />
                    </FormControl>
                    <p className="text-sm text-muted-foreground">
                      Add a video link for your sequence. Supports YouTube, Vimeo, Facebook, TikTok, Instagram, Twitch, and Dailymotion.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* LOR Settings Section - Conditional */}
            {softwareType === 'Light-O-Rama' && (
              <div className="space-y-4">
                <LORSettingsSection 
                  form={form} 
                  isExpanded={showLORSettings}
                  onToggleExpanded={() => setShowLORSettings(!showLORSettings)}
                />
              </div>
            )}

            <Separator />

            {/* Layout Photos Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Layout Photos (Optional)
              </h3>

              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  Upload up to 3 photos showing your xLights or Light-O-Rama layout.
                  These help buyers visualize how your sequence looks on an actual display.
                </p>
              </div>

              <LayoutPhotoUpload
                photos={layoutPhotos}
                onPhotosChange={onLayoutPhotosChange}
                maxPhotos={3}
              />
            </div>

            <Separator />

            {/* Pricing Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Pricing
              </h3>
              
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  Choose whether to offer your sequence for free or set a price. 
                  Free sequences get more downloads and help build your reputation.
                </p>
              </div>

              <FormField
                control={form.control}
                name="is_free"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="flex items-center gap-2">
                        <Gift className="h-4 w-4" />
                        Make this sequence free
                      </FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Free sequences are promoted more and help build your reputation
                      </p>
                    </div>
                  </FormItem>
                )}
              />

              {!isFree && (
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Price (USD) *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                            <Input
                              {...field}
                              type="number"
                              min="0"
                              step="0.01"
                              className="pl-10"
                              value={field.value || ''}
                              onChange={(e) => field.onChange(Number(e.target.value))}
                              placeholder="0.00"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {price > 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-sm mb-3">Pricing Breakdown</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Your price:</span>
                          <span className="font-medium">${price.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-red-600">
                          <span>Platform fee (10%):</span>
                          <span>-${calculatePlatformFee(price).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-medium text-green-600 border-t pt-2">
                          <span>Your earnings:</span>
                          <span>${calculateYourEarnings(price).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={onBack}>
                Back
              </Button>
              <Button type="button" onClick={handleNext}>
                Next: Review & Submit
              </Button>
            </div>
          </div>
        </Form>
      </CardContent>
    </Card>
  );
};

export default DetailsStep; 